import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Camera,
  AlertTriangle,
  Users,
  MapPin,
  Radio,
  RefreshCw,
  Activity,
  Link2,
  Brain,
  Plug,
  Send,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import { SpinLoader } from "../../../components/loader/Loader";
import {
  getCommandCenterCctvGrid,
  getCommandCenterActiveIncidents,
  getCommandCenterGuardDeployment,
  getCommandCenterThreatHeatmap,
  getCommandCenterDashboardSummary,
  sendCommandCenterEmergencyBroadcast,
  getAllAccessZones,
} from "../../../service/securityService";
import {
  Camera as CameraType,
  SecurityIncident,
  CommandCenterCctvGridData,
  CommandCenterGuardDeploymentData,
  CommandCenterThreatHeatmapData,
  CommandCenterDashboardSummaryData,
  GuardDeploymentEntry,
  ThreatHeatmapCell,
  AccessZone,
  EmergencyBroadcastPayload,
} from "../../../types/security_types";

type CctvFilter = "all" | "active" | "maintenance" | "offline";

function cameraKey(c: CameraType): string {
  return String(c._id || c.id || c.cameraId);
}

function cameraStatusNorm(status?: string): string {
  return (status || "").toLowerCase();
}

function statusBadgeClass(status?: string): string {
  const s = cameraStatusNorm(status);
  if (s === "active") return "bg-emerald-600 text-white";
  if (s === "maintenance") return "bg-amber-500 text-white";
  if (s === "offline" || s === "inactive") return "bg-slate-600 text-white";
  return "bg-slate-500 text-white";
}

function riskBarClass(score: number): string {
  if (score >= 70) return "bg-red-500";
  if (score >= 40) return "bg-orange-400";
  if (score >= 15) return "bg-amber-400";
  return "bg-emerald-500";
}

function computeGuardPlot(
  deployments: GuardDeploymentEntry[],
): Array<{ entry: GuardDeploymentEntry; left: number; top: number }> {
  const withCoords = deployments.filter(
    (d) =>
      typeof d.currentLocation?.lat === "number" &&
      typeof d.currentLocation?.lng === "number" &&
      !Number.isNaN(d.currentLocation.lat as number) &&
      !Number.isNaN(d.currentLocation.lng as number),
  );
  if (withCoords.length === 0) return [];
  const lats = withCoords.map((d) => d.currentLocation.lat as number);
  const lngs = withCoords.map((d) => d.currentLocation.lng as number);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  const latSpan = maxLat - minLat || 1e-6;
  const lngSpan = maxLng - minLng || 1e-6;
  return withCoords.map((entry) => {
    const lat = entry.currentLocation.lat as number;
    const lng = entry.currentLocation.lng as number;
    const left = ((lng - minLng) / lngSpan) * 80 + 10;
    const top = (1 - (lat - minLat) / latSpan) * 80 + 10;
    return { entry, left, top };
  });
}

function incidentTime(incident: SecurityIncident): number {
  const raw =
    incident.incidentDateTime || incident.dateTime || incident.createdAt || "";
  const t = new Date(raw as string).getTime();
  return Number.isNaN(t) ? 0 : t;
}

const CommandCenterDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [silentRefresh, setSilentRefresh] = useState(false);
  const [cctv, setCctv] = useState<CommandCenterCctvGridData | null>(null);
  const [incidentsBlock, setIncidentsBlock] = useState<{
    incidents: SecurityIncident[];
    total: number;
  } | null>(null);
  const [guards, setGuards] = useState<CommandCenterGuardDeploymentData | null>(
    null,
  );
  const [heatmap, setHeatmap] = useState<CommandCenterThreatHeatmapData | null>(
    null,
  );
  const [summary, setSummary] =
    useState<CommandCenterDashboardSummaryData | null>(null);
  const [heatmapDays, setHeatmapDays] = useState(30);
  const [cctvFilter, setCctvFilter] = useState<CctvFilter>("all");
  const [locationFilter, setLocationFilter] = useState<string>("all");
  const [selectedCamera, setSelectedCamera] = useState<CameraType | null>(null);
  const [incidentSeverityTab, setIncidentSeverityTab] = useState<
    "all" | "Critical" | "High" | "Medium" | "Low"
  >("all");

  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [broadcastMessage, setBroadcastMessage] = useState("");
  const [broadcastPriority, setBroadcastPriority] =
    useState<EmergencyBroadcastPayload["priority"]>("high");
  const [broadcastAudience, setBroadcastAudience] =
    useState<EmergencyBroadcastPayload["targetAudience"]>("all");
  const [broadcastZoneIds, setBroadcastZoneIds] = useState<string[]>([]);
  const [accessZones, setAccessZones] = useState<AccessZone[]>([]);
  const [broadcastSending, setBroadcastSending] = useState(false);

  const fetchData = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent === true;
    try {
      if (!silent) setLoading(true);
      else setSilentRefresh(true);

      const [cctvRes, incRes, guardRes, summaryRes] = await Promise.all([
        getCommandCenterCctvGrid(),
        getCommandCenterActiveIncidents(),
        getCommandCenterGuardDeployment(),
        getCommandCenterDashboardSummary(),
      ]);

      setCctv(cctvRes);
      if (incRes) {
        const sorted = [...(incRes.incidents || [])].sort(
          (a, b) => incidentTime(b) - incidentTime(a),
        );
        setIncidentsBlock({ incidents: sorted, total: incRes.total });
      } else setIncidentsBlock(null);
      setGuards(guardRes);
      setSummary(summaryRes);
    } catch (error: unknown) {
      console.error("Command center load error:", error);
      toast.error("Failed to load command center data");
    } finally {
      setLoading(false);
      setSilentRefresh(false);
    }
  }, []);

  const fetchHeatmap = useCallback(async () => {
    try {
      const h = await getCommandCenterThreatHeatmap({ days: heatmapDays });
      setHeatmap(h);
    } catch {
      toast.error("Failed to load threat heatmap");
    }
  }, [heatmapDays]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchHeatmap();
  }, [fetchHeatmap]);

  useEffect(() => {
    const id = window.setInterval(() => {
      fetchData({ silent: true });
      fetchHeatmap();
    }, 30000);
    return () => clearInterval(id);
  }, [fetchData, fetchHeatmap]);

  const locationOptions = useMemo(() => {
    if (!cctv?.camerasByLocation) return ["all"];
    return ["all", ...Object.keys(cctv.camerasByLocation).sort()];
  }, [cctv]);

  const filteredCameras = useMemo(() => {
    const list = cctv?.cameras || [];
    let out = list;
    if (locationFilter !== "all") {
      out = cctv?.camerasByLocation?.[locationFilter] || [];
    }
    if (cctvFilter === "all") return out;
    return out.filter((cam) => {
      const s = cameraStatusNorm(cam.status);
      if (cctvFilter === "active") return s === "active";
      if (cctvFilter === "maintenance") return s === "maintenance";
      if (cctvFilter === "offline") return s === "offline" || s === "inactive";
      return true;
    });
  }, [cctv, cctvFilter, locationFilter]);

  const filteredIncidents = useMemo(() => {
    const list = incidentsBlock?.incidents || [];
    if (incidentSeverityTab === "all") return list;
    return list.filter((i) => i.severity === incidentSeverityTab);
  }, [incidentsBlock, incidentSeverityTab]);

  const guardPlots = useMemo(
    () => computeGuardPlot(guards?.guardDeployments || []),
    [guards],
  );

  const openBroadcast = async () => {
    setBroadcastOpen(true);
    try {
      const zones = await getAllAccessZones();
      setAccessZones(Array.isArray(zones) ? zones : []);
    } catch {
      setAccessZones([]);
    }
  };

  const submitBroadcast = async () => {
    const msg = broadcastMessage.trim();
    if (!msg) {
      toast.error("Enter a broadcast message");
      return;
    }
    setBroadcastSending(true);
    try {
      const res = await sendCommandCenterEmergencyBroadcast({
        message: msg,
        priority: broadcastPriority,
        targetAudience: broadcastAudience,
        zones: broadcastZoneIds.length ? broadcastZoneIds : undefined,
      });
      const count = res?.recipientsCount;
      toast.success(
        typeof count === "number"
          ? `Broadcast queued (${count} recipient(s))`
          : "Emergency broadcast sent",
      );
      setBroadcastOpen(false);
      setBroadcastMessage("");
      setBroadcastZoneIds([]);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(
        err?.response?.data?.message || "Failed to send emergency broadcast",
      );
    } finally {
      setBroadcastSending(false);
    }
  };

  const toggleBroadcastZone = (zoneId: string) => {
    setBroadcastZoneIds((prev) =>
      prev.includes(zoneId)
        ? prev.filter((z) => z !== zoneId)
        : [...prev, zoneId],
    );
  };

  const getSeverityColor = (severity: string) => {
    const colors: Record<string, string> = {
      Low: "border-green-500 bg-green-50",
      Medium: "border-yellow-500 bg-yellow-50",
      High: "border-orange-500 bg-orange-50",
      Critical: "border-red-500 bg-red-50",
    };
    return colors[severity] || "border-gray-500 bg-gray-50";
  };

  const ss = cctv?.statusSummary;

  if (loading && !summary && !cctv) {
    return (
      <div className="flex justify-center items-center h-screen">
        <SpinLoader />
      </div>
    );
  }

  return (
    <div className="px-2 py-4 w-full min-h-screen mt-10  text-slate-100">
      <div className="mb-6">
        <div className="flex flex-wrap justify-between items-center gap-4 mb-2">
          <div className="flex items-center gap-3">
            <div className="bg-teal-500/20 p-3 rounded-lg border border-teal-500/40">
              <Radio className="w-6 h-6 text-teal-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-black   tracking-tight">
                Security Command Center
              </h1>
              <p className="text-gray-400 text-sm">
                Live CCTV, incidents, guard deployment, threat heatmap, and
                emergency broadcast (web)
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {silentRefresh && (
              <span className="text-xs text-slate-500 flex items-center gap-1">
                <RefreshCw className="w-3 h-3 animate-spin" /> Updating…
              </span>
            )}
            <button
              type="button"
              onClick={() => fetchData()}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-500 transition-colors text-sm"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button
              type="button"
              onClick={openBroadcast}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-500 transition-colors text-sm"
            >
              <Send className="w-4 h-4" />
              Emergency broadcast
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className=" border border-gray-800 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-md text-gray-500 uppercase tracking-wide">
                Active incidents
              </p>
              <p className="text-2xl font-bold text-black">
                {summary?.incidents?.active ?? incidentsBlock?.total ?? "—"}
              </p>
            </div>
            <AlertTriangle className="w-8 h-8 text-red-400 opacity-80" />
          </div>
        </div>
        <div className=" border border-slate-800 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-md text-gray-500 uppercase tracking-wide">
                Cameras
              </p>
              <p className="text-2xl font-bold text-black">
                {ss ? (
                  <>
                    <span className="text-emerald-400">{ss.active}</span>
                    <span className="text-gray-500 text-lg"> / {ss.total}</span>
                  </>
                ) : (
                  (summary?.cameras?.total ?? "—")
                )}
              </p>
              {ss && (
                <p className="text-[11px] text-gray-500 mt-1">
                  Maint {ss.maintenance} · Off {ss.offline}
                </p>
              )}
            </div>
            <Camera className="w-8 h-8 text-teal-400 opacity-80" />
          </div>
        </div>
        <div className=" border border-gray-800 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-md text-gray-500 uppercase tracking-wide">
                Active guards
              </p>
              <p className="text-2xl font-bold text-black">
                {summary?.guards?.active ?? guards?.totalGuards ?? "—"}
              </p>
            </div>
            <Users className="w-8 h-8 text-cyan-400 opacity-80" />
          </div>
        </div>
        <div className=" border border-gray-800 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-md text-gray-500 uppercase tracking-wide">
                Hot zones ({heatmap?.period ?? `${heatmapDays}d`})
              </p>
              <p className="text-2xl font-bold text-black">
                {heatmap?.totalLocations ?? "—"}
              </p>
              {heatmap?.heatmap?.[0] && (
                <p className="text-[11px] text-gray-500 mt-1 truncate max-w-[180px]">
                  Top: {heatmap.heatmap[0].location} (
                  {heatmap.heatmap[0].riskScore})
                </p>
              )}
            </div>
            <Activity className="w-8 h-8 text-amber-400 opacity-80" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2  border border-slate-800 rounded-xl p-5">
          <div className="flex flex-wrap justify-between items-start gap-3 mb-4">
            <div>
              <h2 className="text-lg font-semibold text-black">
                Live CCTV grid
              </h2>
              <p className="text-xs text-gray-500">
                Showing {filteredCameras.length} feed
                {filteredCameras.length === 1 ? "" : "s"}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {(
                [
                  ["all", "All"],
                  ["active", "Active"],
                  ["maintenance", "Maint"],
                  ["offline", "Offline"],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setCctvFilter(key)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium border ${
                    cctvFilter === key
                      ? "bg-teal-600 border-teal-500 text-white"
                      : "bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="mb-3 flex flex-wrap gap-2 items-center">
            <span className="text-xs text-slate-500">Location:</span>
            <select
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className="bg-slate-800 border border-slate-700 text-sm rounded-md px-2 py-1 text-slate-200 max-w-[220px]"
            >
              {locationOptions.map((loc) => (
                <option key={loc} value={loc}>
                  {loc === "all" ? "All locations" : loc}
                </option>
              ))}
            </select>
          </div>
          {filteredCameras.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-500">
              <Camera className="w-14 h-14 mb-3 opacity-40" />
              <p className="text-sm">No cameras match filters</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredCameras.map((camera) => (
                <button
                  key={cameraKey(camera)}
                  type="button"
                  onClick={() => setSelectedCamera(camera)}
                  className={`relative aspect-video bg-slate-950 rounded-lg border text-left overflow-hidden transition-all hover:border-teal-500/60 ${
                    selectedCamera &&
                    cameraKey(selectedCamera) === cameraKey(camera)
                      ? "border-teal-500 ring-1 ring-teal-500/50"
                      : "border-slate-800"
                  }`}
                >
                  {camera.streamUrl ? (
                    <iframe
                      title={camera.cameraName}
                      src={camera.streamUrl}
                      className="absolute inset-0 w-full h-full border-0 bg-black"
                      allow="autoplay; fullscreen"
                    />
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center p-2">
                      <Camera className="w-8 h-8 text-slate-600 mb-2" />
                      <p className="text-xs text-slate-400 text-center px-1 line-clamp-2">
                        {camera.cameraName}
                      </p>
                      <p className="text-[10px] text-slate-600 mt-1">
                        No stream URL
                      </p>
                    </div>
                  )}
                  <div className="absolute top-2 left-2 right-2 flex justify-between items-start pointer-events-none">
                    <span
                      className={`px-2 py-0.5 text-[10px] font-semibold rounded ${statusBadgeClass(
                        camera.status,
                      )}`}
                    >
                      {camera.status || "?"}
                    </span>
                    {cameraStatusNorm(camera.status) === "active" && (
                      <span className="px-2 py-0.5 bg-red-600 text-white text-[10px] font-bold rounded">
                        LIVE
                      </span>
                    )}
                  </div>
                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/90 to-transparent px-2 py-2 pointer-events-none">
                    <p className="text-xs font-medium text-white truncate">
                      {camera.cameraName}
                    </p>
                    <p className="text-[10px] text-slate-400 truncate flex items-center gap-1">
                      <MapPin className="w-3 h-3 shrink-0" />
                      {camera.location || camera.zoneName || "—"}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className=" border border-slate-800 rounded-xl p-5 flex flex-col min-h-[420px]">
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-lg font-semibold text-black">
              Active incidents
            </h2>
            <button
              type="button"
              onClick={() => navigate("/security/incidents")}
              className="text-xs text-teal-400 hover:text-teal-300"
            >
              Open incidents →
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {(["all", "Critical", "High", "Medium", "Low"] as const).map(
              (tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setIncidentSeverityTab(tab)}
                  className={`px-2 py-0.5 rounded text-[11px] font-medium border ${
                    incidentSeverityTab === tab
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "border-slate-800 text-slate-500 hover:text-slate-300"
                  }`}
                >
                  {tab === "all" ? "All" : tab}
                </button>
              ),
            )}
          </div>
          {filteredIncidents.length === 0 ? (
            <div className="flex flex-col items-center justify-center flex-1 text-slate-500 py-8">
              <AlertTriangle className="w-10 h-10 mb-2 opacity-40" />
              <p className="text-sm">No matching incidents</p>
            </div>
          ) : (
            <div className="space-y-2 overflow-y-auto flex-1 max-h-[640px] pr-1">
              {filteredIncidents.map((incident) => (
                <div
                  key={String(incident._id || incident.id)}
                  className={`p-3 border-l-4 rounded-lg /50 ${getSeverityColor(
                    incident.severity || "Low",
                  )}`}
                >
                  <div className="flex justify-between items-start gap-2 mb-1">
                    <h3 className="font-semibold  text-sm leading-tight">
                      {incident.incidentType}
                    </h3>
                    <span className="text-[10px] text-slate-600 whitespace-nowrap">
                      {new Date(
                        incident.incidentDateTime ||
                          incident.dateTime ||
                          incident.createdAt ||
                          Date.now(),
                      ).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-xs text-slate-700 mb-1 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {incident.location || incident.zone || "—"}
                  </p>
                  <p className="text-[11px] text-slate-600 line-clamp-2">
                    {incident.description}
                  </p>
                  <span className="inline-block mt-1 text-[10px] font-medium text-slate-500">
                    {incident.severity} · {incident.status || "Open"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <div className=" border border-slate-800 rounded-xl p-5">
          <h2 className="text-lg font-semibold text-black mb-4">
            Guard deployment map
          </h2>
          <div className="relative rounded-xl bg-slate-950 border border-slate-800 h-72 overflow-hidden">
            <div className="absolute inset-0 opacity-30 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-teal-900/40 via-transparent to-transparent" />
            <div className="absolute top-3 left-3 text-[10px] text-gray-500 uppercase tracking-wider">
              Relative positions (lat/lng)
            </div>
            {guardPlots.length > 0 ? (
              guardPlots.map(({ entry, left, top }) => (
                <div
                  key={entry.guardId}
                  className="absolute w-3 h-3 -ml-1.5 -mt-1.5 rounded-full bg-cyan-400 border-2 border-white shadow-lg cursor-default group"
                  style={{ left: `${left}%`, top: `${top}%` }}
                  title={`${entry.guardName} (${entry.guardId})`}
                >
                  <div className="hidden group-hover:block absolute z-10 left-4 top-0 w-48 p-2 rounded bg-gray-900 border border-gray-700 text-[11px] text-gray-200 shadow-xl">
                    <p className="font-semibold text-white">
                      {entry.guardName}
                    </p>
                    <p className="text-slate-400">{entry.guardId}</p>
                    {entry.shift && (
                      <p className="mt-1 text-gray-500">Shift: {entry.shift}</p>
                    )}
                    {entry.lastCheckIn && (
                      <p className="text-gray-500">
                        Check-in: {new Date(entry.lastCheckIn).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-gray-600 text-sm px-6 text-center">
                No GPS coordinates on guards. Use the roster below.
              </div>
            )}
          </div>
          <div className="mt-4 space-y-2 max-h-48 overflow-y-auto">
            {(guards?.guardDeployments || []).length === 0 ? (
              <p className="text-sm text-gray-500">No active guards on file.</p>
            ) : (
              (guards?.guardDeployments || []).map((g) => (
                <div
                  key={g.guardId}
                  className="flex flex-wrap justify-between gap-2 text-sm  border border-slate-800 rounded-lg px-3 py-2"
                >
                  <div>
                    <p className="font-medium text-white">{g.guardName}</p>
                    <p className="text-xs text-slate-500">{g.guardId}</p>
                  </div>
                  <div className="text-right text-xs text-slate-400">
                    {g.assignedZones?.length ? (
                      <span>
                        {g.assignedZones.map((z) => z.name).join(", ")}
                      </span>
                    ) : (
                      <span>Unassigned</span>
                    )}
                    {g.lastCheckIn && (
                      <p className="text-slate-500 mt-0.5">
                        In: {new Date(g.lastCheckIn).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className=" border border-slate-800 rounded-xl p-5">
          <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
            <h2 className="text-lg font-semibold text-black">Threat heatmap</h2>
            <select
              value={heatmapDays}
              onChange={(e) => setHeatmapDays(Number(e.target.value))}
              className="bg-gray-800 border border-gray-700 text-sm rounded-md px-2 py-1 text-gray-200"
            >
              <option value={7}>7 days</option>
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
            </select>
          </div>
          <p className="text-xs text-gray-500 mb-3">
            Risk score from incidents and access denials by location (
            {heatmap?.period ?? `last ${heatmapDays} days`})
          </p>
          {!heatmap?.heatmap?.length ? (
            <div className="py-12 text-center text-gray-500 text-sm">
              No heatmap data for this period.
            </div>
          ) : (
            <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
              {(heatmap.heatmap as ThreatHeatmapCell[]).map((cell) => (
                <div key={cell.location}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-300 font-medium truncate pr-2">
                      {cell.location}
                    </span>
                    <span className="text-gray-500 shrink-0">
                      {cell.riskScore} pts
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-800 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${riskBarClass(
                        cell.riskScore,
                      )}`}
                      style={{ width: `${Math.min(100, cell.riskScore)}%` }}
                    />
                  </div>
                  <p className="text-[10px] text-gray-600 mt-0.5">
                    Inc {cell.incidents} · Denied {cell.violations} · Crit{" "}
                    {cell.criticalIncidents} · High {cell.highIncidents}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-6  border border-gray-800 rounded-xl p-5">
        <h2 className="text-lg font-semibold text-black mb-3">
          Related security modules
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          Deeper analytics stay in dedicated areas (not duplicated here).
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={() => navigate("/security/cyber-physical")}
            className="flex items-center gap-2 px-4 py-3 rounded-lg border border-gray-700 flex-1"
          >
            <Link2 className="w-5 h-5 text-teal-400" />
            <span>
              <span className="font-semibold text-black">Cyber-Physical</span>
              <span className="block text-xs text-gray-500">
                Unified dashboard & correlation
              </span>
            </span>
          </button>
          <button
            type="button"
            onClick={() => navigate("/security/ai-predictive")}
            className="flex items-center gap-2 px-4 py-3 rounded-lg border border-gray-700 flex-1 "
          >
            <Brain className="w-5 h-5 text-violet-400" />
            <span>
              <span className="font-semibold text-black">AI & Predictive</span>
              <span className="block text-xs text-gray-500">
                Zone risk & forecasting
              </span>
            </span>
          </button>
          <button
            type="button"
            onClick={() => navigate("/security/integrations")}
            className="flex items-center gap-2 px-4 py-3 rounded-lg border border-gray-700 flex-1"
          >
            <Plug className="w-5 h-5 text-slate-400" />
            <span>
              <span className="font-semibold text-black">Integrations</span>
              <span className="block text-xs text-gray-500">
                APIs & connectors
              </span>
            </span>
          </button>
        </div>
      </div>

      {selectedCamera && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className=" border border-gray-700 rounded-xl max-w-5xl w-full max-h-[90vh] overflow-auto shadow-2xl">
            <div className="flex justify-between items-center px-5 py-3 border-b border-gray-800">
              <h3 className="text-lg font-semibold text-white">
                {selectedCamera.cameraName}
              </h3>
              <button
                type="button"
                onClick={() => setSelectedCamera(null)}
                className="p-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5">
              <div className="bg-black aspect-video rounded-lg overflow-hidden border border-gray-800">
                {selectedCamera.streamUrl ? (
                  <iframe
                    title={selectedCamera.cameraName}
                    src={selectedCamera.streamUrl}
                    className="w-full h-full min-h-[320px] border-0"
                    allow="autoplay; fullscreen"
                  />
                ) : (
                  <div className="w-full h-full min-h-[320px] flex flex-col items-center justify-center text-gray-500">
                    <Camera className="w-16 h-16 mb-3 opacity-50" />
                    <p className="text-sm">No stream URL configured</p>
                    <p className="text-xs mt-2 text-gray-600">
                      {selectedCamera.ipAddress}
                    </p>
                  </div>
                )}
              </div>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm text-gray-400">
                <span>
                  Location:{" "}
                  {selectedCamera.location || selectedCamera.zoneName || "—"}
                </span>
                <span>Type: {selectedCamera.cameraType}</span>
                <span>Status: {selectedCamera.status}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {broadcastOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className=" border border-gray-700 rounded-xl max-w-lg w-full shadow-2xl">
            <div className="flex justify-between items-center px-5 py-3 border-b border-gray-800">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Radio className="w-5 h-5 text-red-400" />
                Emergency broadcast
              </h3>
              <button
                type="button"
                onClick={() => setBroadcastOpen(false)}
                className="p-2 rounded-lg text-gray-400 hover:bg-gray-800"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-xs text-gray-500">
                Sends via configured channels (email / SMS / push per priority).
                Recipients depend on audience.
              </p>
              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  Message *
                </label>
                <textarea
                  value={broadcastMessage}
                  onChange={(e) => setBroadcastMessage(e.target.value)}
                  rows={4}
                  className="w-full  border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600"
                  placeholder="Clear instruction for staff and guards…"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">
                    Priority
                  </label>
                  <select
                    value={broadcastPriority}
                    onChange={(e) =>
                      setBroadcastPriority(
                        e.target.value as EmergencyBroadcastPayload["priority"],
                      )
                    }
                    className="w-full border border-gray-700 rounded-lg px-2 py-2 text-sm text-white"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">
                    Audience
                  </label>
                  <select
                    value={broadcastAudience}
                    onChange={(e) =>
                      setBroadcastAudience(
                        e.target
                          .value as EmergencyBroadcastPayload["targetAudience"],
                      )
                    }
                    className="w-full  border border-gray-700 rounded-lg px-2 py-2 text-sm text-white"
                  >
                    <option value="all">All (guards + staff)</option>
                    <option value="guards">Guards only</option>
                    <option value="staff">Staff only</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  Scope zones (optional)
                </label>
                <div className="max-h-32 overflow-y-auto border border-slate-800 rounded-lg p-2 space-y-1">
                  {accessZones.length === 0 ? (
                    <p className="text-xs text-slate-600">No zones loaded</p>
                  ) : (
                    accessZones.map((z) => {
                      const id = String(z._id || z.id || "");
                      const name = z.zoneName || id;
                      return (
                        <label
                          key={id || name}
                          className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={broadcastZoneIds.includes(id)}
                            onChange={() => toggleBroadcastZone(id)}
                            className="rounded border-slate-600"
                          />
                          {name}
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setBroadcastOpen(false)}
                  className="px-4 py-2 rounded-lg text-sm text-slate-400 hover:bg-slate-800"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={broadcastSending}
                  onClick={submitBroadcast}
                  className="px-4 py-2 rounded-lg text-sm bg-red-600 text-white hover:bg-red-500 disabled:opacity-50 flex items-center gap-2"
                >
                  {broadcastSending ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Send broadcast
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommandCenterDashboard;
